import { useState, useEffect, CSSProperties } from 'react';

import {
    TileMeta,
    TilesMeta,
} from '@/scenarios/legacy-player/components/feature-player/types/tiles';

import { getCurrentTile } from './getCurrentTile';

const getTile = (time: number, spriteConfig: TileMeta, isHighResFromStreams: boolean) => {
    const {
        tiles: { rows, columns },
        uriTemplate,
        tileDuration,
    } = spriteConfig;
    const tilesInSprite = rows * columns;
    const tileIndex = Math.floor(time / tileDuration);
    const tileIndexInSprite = tileIndex % tilesInSprite;
    const tileRow = Math.floor(tileIndexInSprite / columns);
    const tileCol = tileIndexInSprite % columns;
    const spriteIndex = Math.floor(tileIndex / tilesInSprite);
    const spriteUrl = uriTemplate.replace('$Number$', String(spriteIndex));

    const width = isHighResFromStreams
        ? spriteConfig.tileSize.width / 2
        : spriteConfig.tileSize.width;
    const height = isHighResFromStreams
        ? spriteConfig.tileSize.height / 2
        : spriteConfig.tileSize.height;
    const fullHeight = spriteConfig.tileSize.height * spriteConfig.tiles.rows;
    const resolutionHeight = isHighResFromStreams ? fullHeight / 2 : fullHeight;

    const backgroundPosition = `-${tileCol * width}px -${tileRow * height}px`;
    const backgroundSize = `${width * spriteConfig.tiles.columns}px ${resolutionHeight}px`;

    return {
        backgroundPosition,
        backgroundSize,
        spriteUrl,
        width,
        height,
    };
};

export interface Preview extends CSSProperties {
    width: number;
    height: number;
}

export const usePreview = ({
    time,
    config,
    isMetaFromStreams,
}: {
    time: number | null;
    config: TilesMeta[] | null;
    isMetaFromStreams: boolean;
}): Preview | null => {
    const [loadedSprites, setLoadedSprites] = useState<Set<string>>(new Set());

    const spritesToLoad: string[] = [];
    let previewResult: ({ width: number; height: number } & CSSProperties) | null = null;

    if (time !== null && config) {
        const { modifiedTime, tile } = getCurrentTile({
            time,
            tiles: config,
        });

        if (tile) {
            const spriteConfigs = [tile.lowRes, tile.highRes];
            for (let i = 0; i < spriteConfigs.length; i += 1) {
                const spriteConfig = spriteConfigs[i];
                if (spriteConfig) {
                    // Проверка на lowRes. Если hight сокращаем ширину и высоту в 2 раза (бэк отдвет большые спрайты)
                    const isHighResFromStreams = i === 1 && isMetaFromStreams;

                    const { spriteUrl, backgroundPosition, backgroundSize, width, height } =
                        getTile(modifiedTime, spriteConfig, isHighResFromStreams);

                    spritesToLoad.push(spriteUrl);

                    if (loadedSprites.has(spriteUrl)) {
                        previewResult = {
                            background: `url(${spriteUrl}) no-repeat ${backgroundPosition} / ${backgroundSize}`,
                            width,
                            height,
                        };
                    }
                } else {
                    break;
                }
            }
        }
    }

    useEffect(() => {
        let actual = true;
        spritesToLoad.forEach((spriteUrl) => {
            const image = new Image();
            const imageReadyCallback = () => {
                if (!actual) {
                    return;
                }
                setLoadedSprites((set) => {
                    if (!set.has(spriteUrl)) {
                        return new Set([...set, spriteUrl]);
                    }
                    return set;
                });
            };
            if (image.decode) {
                image.src = spriteUrl;
                image
                    .decode()
                    .then(imageReadyCallback)
                    .catch(() => {});
            } else {
                image.onload = imageReadyCallback;
                image.src = spriteUrl;
            }
        });
        return () => {
            actual = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [spritesToLoad.join('')]);

    return previewResult;
};
